<template>
    <div class="ufo-sec ufo-sec--projectgrid">
        <ToggleGrid />
        <SectionBackground :section="section" />
        <div v-bind:key="section" v-for="section in filteredHomePageSections" class="grid">
            <div :class="'works_line_'+ portfolio.templateSettings.theme"></div>
        </div>

        <div class="grid-wrapper">
            <div
                class="grid ufo--user-projects"
                :class="{
                'text-grid-layout': layout === 'text-grid',
                'text-grid-layout--single-column': layout === 'text-grid' && thumbnailGridColumns === 1,
                'text-list-layout': layout === 'text-list'
            }"
                :style="textGridStyle"
            >
                <div v-bind:key="index" v-for="(caseStudy, index) in caseStudies" @click="(evt) => navigateToCaseStudy(evt, caseStudy)" @mouseenter="emitter.emit('project-hover', true)" @mouseleave="emitter.emit('project-hover', false)"
                   :class="['project',
                          'title-is--' + (portfolio.templateSettings.projectTitle.visibility ? portfolio.templateSettings.projectTitle.visibility : 'none'),
                          'title-is--' + (portfolio.templateSettings.projectTitle.placement ? portfolio.templateSettings.projectTitle.placement : 'none'),
                          'title-is--' + (portfolio.templateSettings.projectTitle.color ? portfolio.templateSettings.projectTitle.color : 'default'),
                          'thumbnail-is--' + (portfolio.templateSettings.tileDisplayMode ? portfolio.templateSettings.tileDisplayMode.split('-')[0] : 'default'),
                          'thumbnail-is--' + (portfolio.templateSettings.tileDisplayMode ? portfolio.templateSettings.tileDisplayMode : 'default'),
                          'title-align--' + titleVerticalAlignment,
                          'title-justify--' + (portfolio.templateSettings.projectTitle.alignment.horizontal ? portfolio.templateSettings.projectTitle.alignment.horizontal : 'none'),
                          'layout-is--' + layout,
                          cardType,
                          outlineWeight,
                          interactionClass,
                        ]"
                    :style="[dividerLineStyle, cornerRadiusStyle, arrowStyle]"
                >
                    <AnimationWrapper
                        :disable-animation="portfolio.templateSettings.projectTitle.placement === 'on-thumbnail' && portfolio.templateSettings.projectTitle.visibility === 'hover'"
                    >
                        <template #animationContent>
                            <div :class="projectNameClasses(caseStudy)">
                                <div
                                    v-if="showLabels && (caseStudy.labels && caseStudy.labels.length)"
                                    :class="['ufo--project-title-container', 'project-labels', `align-${buttonAlign}`]"
                                >
                                    <span
                                        v-for="(label, labelIndex) in caseStudy.labels"
                                        :key="`project-label-${labelIndex}`"
                                        :class="['project-label', labelStyle.fillStyle, labelStyle.shape]"
                                    >
                                        {{ label?.text || '' }}
                                    </span>
                                </div>
                                <div
                                    class="ufo--project-title-container project-text"
                                    role="button"
                                    tabindex="0"
                                    :aria-label="projectActionLabel(caseStudy)"
                                    @keydown.enter.prevent="navigateToCaseStudy($event, caseStudy)"
                                    @keydown.space.prevent="navigateToCaseStudy($event, caseStudy)"
                                >
                                    <div class="project-title"><span class="project-title-underline">{{projectName(caseStudy)}}</span></div>
                                    <div v-if="!portfolio.templateSettings.projectTitle.hideDescription" class="project-subtitle">{{projectSubtitle(caseStudy)}}</div>
                                </div>
                                <div
                                    v-if="showProjectGridButton"
                                    :class="['ufo--project-title-container', 'project-grid-button', `align-${buttonAlign}`]"
                                >
                                    <CtaButton
                                        :section="projectGridButtonSection(caseStudy)"
                                        :alignment="portfolio.templateSettings.projectTitle.alignment.horizontal"
                                        :form-submit-button="false"
                                    />
                                </div>
                            </div>
                        </template>
                    </AnimationWrapper>
                    <AnimationWrapper>
                        <template #animationContent>
                            <ProjectThumbnail v-if="!isTextOnlyLayout" :case-study="caseStudy" :is-first="index === 0" />
                        </template>
                    </AnimationWrapper>
                    <div v-if="interactionType === 'show-arrow'" class="project-arrow-badge">
                        <Icon icon-name="arrow-out" :width="28" :height="28" />
                    </div>
                </div>
            </div>
        </div>
        <EmptyProjectGrid v-if="(caseStudies.length < 1) && loggedInUserId && loggedInUserId === user._id && !isReview && !isJFC" />
    </div>
</template>

<script>
    import ToggleGrid from "../../../common/components/ToggleGrid.vue";
    import ProjectThumbnail from "../../../../scripts/sections/projectGrid/ProjectThumbnail.vue";
    import AnimationWrapper from "../../../common/components/AnimationWrapper.vue";
    import EmptyProjectGrid from "./_helper/EmptyProjectGrid.vue";
    import {
        convertRichTextHTMLToPlainText
    } from "@/scripts/common/components/textEditor/utilities/textConverterUtilities.js";
    import {mapGetters} from "vuex";
    import SectionBackground from "../../../sections/common/SectionBackground.vue";
    import CtaButton from "./_helper/CtaButton.vue";
    import Icon from "../../../common/Icon.vue";
    import {SectionTypesEnum} from "@/scripts/common/enums.js";
    import {generatedPreviewNavigationTarget} from "@/scripts/adminSite/aiPortfolioGenerator/portfolioPreviewCache.js";

    export default {
        name: "ProjectGrid",
        components: {SectionBackground, EmptyProjectGrid, AnimationWrapper, ProjectThumbnail , ToggleGrid, CtaButton, Icon },
        props: ['section'],
        computed: {
            ...mapGetters("view", ["viewData", "caseStudyRoute"]),
            portfolio() {
                return this.viewData.portfolio
            },
            user() {
                return this.viewData.user
            },
            caseStudies() {
                return this.viewData.caseStudies
                    .filter(project => !project.draft && !project.deletedAt)
                    .sort((a, b) => a.order - b.order)
            },
            loggedInUserId() {
              return this.viewData.loggedInUserId
            },
            filteredHomePageSections() {
                return this.viewData.portfolioPages.find(project => project.type === "home").sections.filter(section => section.type !== "ProjectGrid" && section.index < section.index && !section.deletedAt)
            },
            layout() {
                return this.portfolio.templateSettings.thumbnailLayout ? this.portfolio.templateSettings.thumbnailLayout : this.portfolio.templateSettings.virtualThumbnailLayout
            },
            titleVerticalAlignment() {
                if (['above', 'below'].includes(this.portfolio.templateSettings?.projectTitle?.placement) && this.layout !== 'text-grid') {
                    return 'top';
                }
                return this.portfolio.templateSettings.projectTitle.alignment.vertical || 'none';
            },
            thumbnailGridColumns() {
                return this.portfolio.templateSettings?.thumbnailGridColumns ?? 2;
            },
            textGridStyle() {
                if (this.layout !== 'text-grid') {
                    return {};
                }
                return {
                    '--thumbnailGridColumns': this.thumbnailGridColumns,
                };
            },
            isReview() {
                return window.location.pathname.includes('/review/') || window.location.pathname.includes('/myreviews/')
            },
            isJFC() {
                return window.location.pathname.includes('/jobfitchecker/')
            },
            cardType() {
                return this.portfolio.templateSettings?.cardStyle?.cardType
                    ? `card-type-${this.portfolio.templateSettings?.cardStyle?.cardType}`
                    : '';
            },
            cornerRadiusStyle() {
                const cardStyle = this.portfolio.templateSettings?.cardStyle || {};
                const radiusPx = cardStyle.cornerRadiusPx ?? 0;

                return { '--borderRadius': `${radiusPx}px` };
            },
            arrowStyle() {
                const cardStyle = this.portfolio.templateSettings?.cardStyle || {};
                return {
                    '--projectArrowColor': this.resolveCardStyleColor(cardStyle.arrowColor, '#0F0F0F'),
                    '--projectArrowBgColor': this.resolveCardStyleColor(cardStyle.arrowBgColor, '#EEEEEE'),
                    '--projectImageBackdropColor': this.resolveCardStyleColor(cardStyle.imageBackdropColor, '#DCFA37'),
                    '--projectTextBackdropColor': this.resolveCardStyleColor(cardStyle.textBackdropColor, '#101472'),
                };
            },
            outlineWeight() {
                return this.portfolio.templateSettings?.cardStyle?.outlineWeight
                    ? `outline-weight-${this.portfolio.templateSettings?.cardStyle?.outlineWeight}`
                    : '';
            },
            interactionClass() {
                return `interaction-is--${this.interactionType}`;
            },
            interactionType() {
                const configured = this.portfolio.templateSettings?.cardStyle?.interaction;
                if (configured) return configured;
                return this.cardTypeValue === 'none' ? 'zoom-in' : 'card-color-change';
            },
            cardTypeValue() {
                return this.portfolio.templateSettings?.cardStyle?.cardType || 'none';
            },
            isTextListLayout() {
                return this.layout === 'text-list';
            },
            dividerLineStyle() {
                if (this.isTextListLayout && this.cardTypeValue === 'none') {
                    const weight = this.portfolio.templateSettings?.cardStyle?.dividerLineWeight || 1;
                    return { '--dividerLineWeight': `${weight}px` };
                }
                return {};
            },
            labelStyle() {
                return this.portfolio?.templateSettings?.labelStyle || { fillStyle: 'solid', shape: 'pill' };
            },
            buttonAlign() {
                return this.portfolio.templateSettings.projectTitle?.alignment?.horizontal || 'left';
            },
            showProjectGridButton() {
                const hideButton = this.portfolio.templateSettings.projectTitle.hideButton ?? true;
                return !hideButton;
            },
            showLabels() {
                return !this.portfolio.templateSettings.projectTitle.hideLabels;
            },
            isTextOnlyLayout() {
                return this.layout === 'text-grid' || this.layout === 'text-list';
            },
            projectNameClasses() {
                return (caseStudy) => {
                    if (!this.isTextListLayout) {
                        return 'project-name';
                    }

                    const hasLabels = this.showLabels && (caseStudy.labels && caseStudy.labels.length);
                    const hasButton = this.showProjectGridButton;
                    const classes = ['project-name', 'project-name--text-list'];

                    if (hasLabels && hasButton) {
                        classes.push('project-name--labels-text-button');
                    } else if (hasLabels) {
                        classes.push('project-name--labels-text');
                    } else if (hasButton) {
                        classes.push('project-name--text-button');
                    } else {
                        classes.push('project-name--text-only');
                    }

                    return classes;
                };
            },
        },
        methods: {
            resolveCardStyleColor(value, fallbackColor) {
                if (value.paletteColorIndex !== null) {
                    return this.portfolio?.designSettings?.colorPalette?.colors?.[value.paletteColorIndex] || fallbackColor;
                }
                return value.color || fallbackColor;
            },
            navigateToCaseStudy(evt, caseStudy) {
                if(this.$route.query.templatePreview || this.$route.path.startsWith("/portfolio-generation/")) {
                    const previewTarget = generatedPreviewNavigationTarget(
                        this.$route,
                        this.portfolio?.shortId,
                        this.caseStudyRoute(caseStudy.shortId),
                        caseStudy.shortId
                    );
                    if (!previewTarget) return;

                    evt.preventDefault();
                    this.$router.push(previewTarget).then(() => {
                        window.scrollTo(0, 0);
                    });
                    return;
                }
                evt.preventDefault()
                const caseStudyRoute = this.caseStudyRoute(caseStudy.shortId);
                if (!caseStudyRoute) return;

                this.$router.push(caseStudyRoute).then(() => {
                    window.scrollTo(0, 0);
                });
            },
            projectName(caseStudy) {
                let hero = caseStudy.sections.find(s => s?.type === SectionTypesEnum.MAIN_HEADER);
                return caseStudy.name != null
                    ? caseStudy.name
                    : convertRichTextHTMLToPlainText(hero.title.text);
            },
            projectSubtitle(caseStudy) {
                let hero = caseStudy.sections.find(s => s?.type === SectionTypesEnum.MAIN_HEADER);
                return caseStudy.subtitle != null
                    ? caseStudy.subtitle
                    : convertRichTextHTMLToPlainText(hero.subtitle.text)
            },
            projectGridButtonSection(caseStudy) {
                const cta = caseStudy.cta || {};
                return {
                    cta: {
                        show: true,
                        title: cta.title || "View project",
                        type: cta.type || "secondary",
                        url: null
                    }
                };
            },
            projectActionLabel(caseStudy) {
                const name = this.projectName(caseStudy)?.trim();
                return name ? `Open case study ${name}` : 'Open case study';
            }
        }
    }
</script>
